import {
  Channel,
  ChannelMembership,
  ChannelReadReceipt,
  MsgBoardMessage,
  messageBoardUserType,
} from 'domains/messaging/model/messageBoard';

function timestampValue(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * The effective read cutoff is the newer of `lastReadAt` and the timestamp
 * of `lastReadMessageId`. The two fields can briefly disagree when another
 * client advances the receipt, so aligned uses the newer value everywhere it
 * derives unread state.
 */
export function resolveMessageBoardReadCutoff(params: {
  lastReadAt?: string | null;
  lastReadMessageAt?: string | null;
}): string | null {
  const { lastReadAt, lastReadMessageAt } = params;
  if (!lastReadAt) return lastReadMessageAt ?? null;
  if (!lastReadMessageAt) return lastReadAt;

  const readAtValue = timestampValue(lastReadAt);
  const messageAtValue = timestampValue(lastReadMessageAt);
  if (readAtValue === null || messageAtValue === null) return lastReadAt;
  return messageAtValue > readAtValue ? lastReadMessageAt : lastReadAt;
}

/** Newer receipt wins; ties use the receipt id as a stable tie-break. */
export function isReadReceiptNewer(
  candidate: ChannelReadReceipt,
  current: ChannelReadReceipt,
): boolean {
  const candidateAt = timestampValue(candidate.lastReadAt);
  const currentAt = timestampValue(current.lastReadAt);
  if (candidateAt === null) return false;
  if (currentAt === null) return true;
  if (candidateAt === currentAt) return candidate.id > current.id;
  return candidateAt > currentAt;
}

/** Collapse active receipts to the same one-per-channel view as aligned. */
export function latestReadReceiptsByChannel(
  receipts: ChannelReadReceipt[],
): Map<string, ChannelReadReceipt> {
  const byChannel = new Map<string, ChannelReadReceipt>();
  for (const receipt of receipts) {
    if (!receipt.channelId) continue;
    const existing = byChannel.get(receipt.channelId);
    if (!existing || isReadReceiptNewer(receipt, existing)) {
      byChannel.set(receipt.channelId, receipt);
    }
  }
  return byChannel;
}

/** Count a loaded message window with aligned's participation rules. */
export function countUnreadMessagesFromCache(params: {
  messages: MsgBoardMessage[];
  userId: string;
  cutoffAt?: string | null;
}): number {
  const cutoff = timestampValue(params.cutoffAt);
  const participatingThreadIds = new Set<string>();
  for (const message of params.messages) {
    if (message.authorId !== params.userId) continue;
    participatingThreadIds.add(message.parentMessageId || message.id);
  }

  return params.messages.filter((message) => {
    if (message.authorId === params.userId || message.deprecatedOn) return false;
    const createdAt = timestampValue(message.createdAt);
    if (createdAt === null) return false;
    if (cutoff !== null && createdAt <= cutoff) return false;
    if (!message.parentMessageId) return true;
    return participatingThreadIds.has(message.parentMessageId);
  }).length;
}

/** Whether aligned would issue a Firestore unread query for this channel. */
export function shouldQueryMessageBoardUnreadCount(params: {
  channel: Channel;
  wasRecentlyMarkedRead: boolean;
  isQueryBackedOff?: boolean;
  cutoffAt?: string | null;
}): boolean {
  if (params.wasRecentlyMarkedRead || params.isQueryBackedOff) return false;
  const lastActivityAt = params.channel.lastMessageAt ?? params.channel.createdAt;
  if (!lastActivityAt || !params.cutoffAt) return true;
  const activity = timestampValue(lastActivityAt);
  const cutoff = timestampValue(params.cutoffAt);
  if (activity === null || cutoff === null) return true;
  return activity > cutoff;
}

/**
 * Aligned records the newest message currently loaded for the channel. The
 * requested id is only a fallback for callers that do not have a loaded
 * message window yet.
 */
export function resolveMessageBoardReadMessageId(params: {
  requestedMessageId?: string | null;
  cachedMessages?: MsgBoardMessage[];
}): string | null {
  const newestLoadedId = params.cachedMessages?.[0]?.id;
  if (newestLoadedId) return newestLoadedId;
  return params.requestedMessageId || null;
}

/**
 * Timestamp-level fallback for sidebar unread derivation: a channel has
 * "unread" content when its `lastMessageAt` is newer than the effective
 * read cutoff for that channel. Exact message counts are reconciled by the
 * data/view-model layer using the helpers below.
 *
 * A channel with no `lastMessageAt` (never had a message) is never unread.
 * A channel with activity but no receipt is unread (never opened).
 *
 */
export function computeChannelHasUnread(
  channel: Channel,
  receipt: ChannelReadReceipt | null,
  lastReadMessageAt?: string | null,
): boolean {
  if (!channel.lastMessageAt) return false;
  if (!receipt) return true;
  const cutoff = resolveMessageBoardReadCutoff({
    lastReadAt: receipt.lastReadAt,
    lastReadMessageAt,
  });
  if (!cutoff) return true;
  const activity = timestampValue(channel.lastMessageAt);
  const cutoffValue = timestampValue(cutoff);
  if (activity === null || cutoffValue === null) return channel.lastMessageAt > cutoff;
  return activity > cutoffValue;
}

/**
 * Whether a user (with a specific membership) is allowed to post to a channel
 * — ported from aligned's `canUserSendMessage`, minus the DM-first-message
 * branch (deferred to the DMs sub-slice) and the pre-flight `canUserAccessChannel`
 * check (the caller already has a live membership → access is implied).
 *
 * Rules (matching aligned exactly):
 *  - No membership (or a deprecated one) → false.
 *  - Role `owner` or `admin` → always true.
 *  - Moderator or employee → `channel.allowModeratorPosts` (beta addition:
 *    aligned only checked `isModerator`; employees post to moderator-only
 *    boards like Announce).
 *  - Plain member → `channel.allowMemberPosts`.
 */
export function canUserSendGroupMessage(
  channel: Channel,
  membership: ChannelMembership | null,
): boolean {
  if (!membership || membership.deprecatedOn) return false;
  if (membership.role === 'owner' || membership.role === 'admin') return true;
  if (messageBoardUserType(membership) !== 'member') return channel.allowModeratorPosts;
  return channel.allowMemberPosts;
}
